import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { FaTiktok } from "react-icons/fa";
import { FaWhatsapp } from "react-icons/fa6";
import { z } from "zod";

import Facebook from "~/assets/icons/contactFacebook.svg";
import Instagram from "~/assets/icons/contactInstagram.svg";
import X from "~/assets/icons/contactX.svg";
import { Loader2, Mail, Phone } from "~/components/icons";
import { Button } from "@workspace/web-ui/components/button";
import { Input } from "@workspace/web-ui/components/input";
import { Label } from "@workspace/web-ui/components/label";
import { Textarea } from "@workspace/web-ui/components/textarea";
import { useToast } from "@workspace/web-ui/hooks/use-toast";

import { useContactUsMutation } from "../../api/mutations";

// Define the schema using zod
const schema = z.object({
  fullName: z.string().min(1, "Full name is required"),
  email: z.string().email("Invalid email address"),
  subject: z.string().min(1, "Subject is required"),
  message: z.string().min(1, "Message is required"),
});

type FormData = z.infer<typeof schema>;

export default function ContactForm() {
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const { mutate, isPending } = useContactUsMutation();

  const onSubmit = (data: FormData) => {
    mutate(
      {
        full_name: data.fullName,
        email: data.email,
        subject: data.subject,
        message: data.message,
      },
      {
        onSuccess: (data) => {
          if (!data.success) {
            toast({
              title: "An error occurred while submitting the form",
              description: data?.error,
              variant: "destructive",
            });
          }
          toast({
            title: "Message sent successfully",
            description: "We will get back to you shortly",
          });
          reset();
        },
        onError: (error) => {
          console.log(error);
        },
      },
    );
  };

  return (
    <div className="mx-auto w-full max-w-5xl overflow-hidden rounded-lg pt-20">
      <div className="flex flex-col-reverse md:flex-row">
        {/* Contact Info Section */}
        <div className="p-8 md:w-2/5">
          <div className="space-y-6">
            <h2 className="text-xl font-medium text-gray-700">Get in touch</h2>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Mail className="text-[#363840]" />
                <span className="text-sm font-semibold text-[#2D2F35]">
                  appointments@proxymedicine.com
                </span>
              </div>

              <div className="flex items-center gap-3">
                <Phone className="text-[#363840]" />
                <span className="text-sm font-semibold text-[#2D2F35]">
                +1 (888) 779-9633 
                </span>
              </div>

              <div className="flex items-center gap-3">
                <FaWhatsapp className="text-xl text-[#363840]" />
                <span className="text-sm font-semibold text-[#2D2F35]">
                +1 (888) 779-9633 
                </span>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">Follow us</h3>
              <div className="flex items-center gap-4">
                <a
                  href="http://tiktok.com/@proxymedicine"
                  className="rounded-full bg-[#0000001F] p-2 text-gray-600 hover:text-gray-900"
                >
                  <FaTiktok className="size-4" />
                  <span className="sr-only">Tiktok</span>
                </a>
                <a
                  href="https://www.instagram.com/proxymedicine?igsh=MnlsajcwaTBrbW9q"
                  className="rounded-full bg-[#0000001F] p-2 text-gray-600 hover:text-gray-900"
                >
                  <img src={Instagram} alt="" className="size-4" />
                  <span className="sr-only">Instagram</span>
                </a>
                <a
                  href="https://x.com/ProxyMed_?s=09"
                  className="rounded-full bg-[#0000001F] p-2 text-gray-600 hover:text-gray-900"
                >
                  <img src={X} alt="" className="size-4" />
                  <span className="sr-only">Twitter (X)</span>
                </a>
                <a
                  href="https://www.facebook.com/share/18hR8tedoR/"
                  className="rounded-full bg-[#0000001F] p-2 text-gray-600 hover:text-gray-900"
                >
                  <img src={Facebook} alt="" className="size-4" />
                  <span className="sr-only">Facebook</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div className="bg-white p-8 md:w-3/5">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <Label htmlFor="fullName" className="text-gray-600">
                Full name
              </Label>
              <Input
                id="fullName"
                {...register("fullName")}
                className="mt-1 rounded-md border-gray-200"
              />
              {errors.fullName && (
                <p className="text-red-500">{errors.fullName.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="email" className="text-gray-600">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                {...register("email")}
                className="mt-1 rounded-md border-gray-200"
              />
              {errors.email && (
                <p className="text-red-500">{errors.email.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="subject" className="text-gray-600">
                Subject
              </Label>
              <Input
                id="subject"
                {...register("subject")}
                className="mt-1 rounded-md border-gray-200"
              />
              {errors.subject && (
                <p className="text-red-500">{errors.subject.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="message" className="text-gray-600">
                Message
              </Label>
              <Textarea
                id="message"
                {...register("message")}
                className="mt-1 min-h-[120px] rounded-md border-gray-200"
              />
              {errors.message && (
                <p className="text-red-500">{errors.message.message}</p>
              )}
            </div>

            <Button
              type="submit"
              className="h-[57px] w-full rounded-[8px] bg-[#4272DD] font-medium text-white hover:bg-blue-600"
              disabled={!isValid || isPending}
            >
              SUBMIT
              {isPending && (
                <Loader2
                  className="ml-2 animate-spin"
                  size={16}
                  color="white"
                  aria-hidden="true"
                />
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
